import { useEffect, useState, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { updateSite } from '../../services/siteService';
import type { Site } from '../../types/project';
import { COUNTRIES, provincesForCountry } from '../../constants/geography';

interface EditSiteDialogProps {
  open: boolean;
  site: Site | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditSiteDialog = ({ open, site, onClose, onUpdated }: EditSiteDialogProps) => {
  const [siteName, setSiteName] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [province, setProvince] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const provinceOptions = useMemo(() => provincesForCountry(country), [country]);

  useEffect(() => {
    if (site && open) {
      setSiteName(site.SiteName || '');
      setSiteCity(site.SiteCity || '');
      setCountry(site.Country || null);
      setProvince(site.Province || null);
      setError('');
    }
  }, [site, open]);

  const handleSubmit = async () => {
    if (!site) return;
    if (!siteName.trim()) { setError('Site Name is required'); return; }
    if (!siteCity.trim() && !province && !country) {
      setError('Please enter at least a City, Province, or Country.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await updateSite(site.SiteKey, {
        SiteName: siteName.trim(),
        SiteCity: siteCity.trim() || null,
        Province: province,
        Country: country,
      });
      onUpdated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to update site.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!site) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Edit Site</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Site Name" required value={siteName}
          onChange={(e) => setSiteName(e.target.value)} />
        <TextField size="small" label="City" value={siteCity}
          onChange={(e) => setSiteCity(e.target.value)}
          helperText="Optional" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Autocomplete size="small" freeSolo options={COUNTRIES}
            value={country}
            onChange={(_e, v) => { setCountry(typeof v === 'string' ? v : v); }}
            onInputChange={(_e, v) => setCountry(v || null)}
            renderInput={(params) => <TextField {...params} label="Country" />} />
          <Autocomplete size="small" freeSolo options={provinceOptions}
            value={province}
            onChange={(_e, v) => setProvince(typeof v === 'string' ? v : v)}
            onInputChange={(_e, v) => setProvince(v || null)}
            renderInput={(params) => <TextField {...params} label="Province / State" />} />
        </Box>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
